import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { customerFormsApi } from "../../shared/customerFormsApi";
import {
  formActionLabels,
  formatFormTime,
  formTypeLabel,
  statusLabels,
} from "../../shared/customerForms";
import type { CustomerFormDetail, FormStatus, FormType } from "../../types/forms";
import FormStatusBadge from "../../components/customerForms/FormStatusBadge";
import FormActionDialog from "../../components/customerForms/FormActionDialog";
import EmptyModal from "../../components/modals/EmptyModal";
import { buttonClass, secondaryClass } from "../../components/customerForms/FormControls";

export default function FormDetailScreen() {
  const { type, id } = useParams();
  const location = useLocation();
  const [form, setForm] = useState<CustomerFormDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [action, setAction] = useState<FormStatus | "reschedule" | "reopen" | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setForm(null);
    if (!["credit", "demo"].includes(type || "") || !id) {
      setError("Form not found.");
      setLoading(false);
      return;
    }
    customerFormsApi.detail(type as FormType, id).then((result) => {
      if (!active) return;
      setLoading(false);
      if (result.success) setForm(result.data);
      else setError(result.data.message || "Could not load this form.");
    });
    return () => {
      active = false;
    };
  }, [type, id, revision]);
  const origin = location.state?.returnTo;
  const returnTo =
    typeof origin === "string" && /^\/(forms|customers)(\/|\?|$)/.test(origin) ? origin : "/forms";
  return (
    <section className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <Link to={returnTo} className="text-primary-700 underline">
        ← Back to forms
      </Link>
      {location.state?.submitted && (
        <p role="status" className="rounded-lg bg-green-50 p-4 text-green-800">
          Request submitted. Track its status and activity below.
        </p>
      )}
      {loading && <p role="status">Loading form…</p>}
      {error && (
        <p role="alert" className="text-red-700">
          {error}{" "}
          <button className="underline" onClick={() => setRevision((v) => v + 1)}>
            Try again
          </button>
        </p>
      )}
      {form && (
        <>
          <header className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-sm text-gray-500">{formTypeLabel(form.type)}</p>
              <h1 className="mt-1 text-2xl font-semibold">{form.reference}</h1>
              <Link
                className="text-primary-700 underline"
                to={`/customers/${form.customer_id}?tab=forms`}
              >
                {form.customer_name}
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <FormStatusBadge status={form.status} />
              <button className={secondaryClass} onClick={() => setRevision((v) => v + 1)}>
                Refresh
              </button>
            </div>
          </header>
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <dl className="grid gap-5 sm:grid-cols-2">
              {[
                ["Submitted by", form.submitted_by],
                ["Submitted", formatFormTime(form.created_at)],
                ["Last updated", formatFormTime(form.updated_at)],
                [
                  form.type === "credit" ? "Invoice number" : "Event name",
                  form.invoice_number || form.event_name,
                ],
                ["Requested event date", form.event_date],
                ["Scheduled date", form.scheduled_date],
                ["Credit reference", form.credit_reference],
                ["Issue date", form.issued_date],
              ]
                .filter(([, value]) => value)
                .map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-sm text-gray-500">{label}</dt>
                    <dd className="mt-1 font-medium">{value}</dd>
                  </div>
                ))}
            </dl>
            <h2 className="mt-6 text-sm text-gray-500">Description</h2>
            <p className="mt-1 whitespace-pre-wrap break-words">{form.description}</p>
          </div>
          {(form.allowed_actions.length > 0 || form.can_reschedule || form.can_reopen) && (
            <div className="flex flex-wrap gap-3">
              {form.allowed_actions.map((status) => (
                <button
                  key={status}
                  className={
                    status === "cancelled" || status === "rejected" ? secondaryClass : buttonClass
                  }
                  onClick={() => setAction(status)}
                >
                  {formActionLabels[status]}
                </button>
              ))}
              {form.can_reopen && (
                <button className={secondaryClass} onClick={() => setAction("reopen")}>
                  Reopen request
                </button>
              )}
              {form.can_reschedule && (
                <button className={secondaryClass} onClick={() => setAction("reschedule")}>
                  Reschedule Demo
                </button>
              )}
            </div>
          )}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="text-lg font-semibold">Activity history</h2>
            <ol className="mt-5 space-y-6">
              {form.history.map((event) => (
                <li key={event.id} className="border-l-2 border-primary-200 pl-4">
                  <p className="font-medium">
                    {event.event === "reopened" && "Request reopened · "}
                    {event.event === "submitted"
                      ? "Submitted"
                      : event.event === "historical_submission"
                        ? "Historical submission record"
                        : event.event === "rescheduled"
                          ? "Demo rescheduled"
                          : `${event.from_status ? statusLabels[event.from_status] + " → " : ""}${event.to_status ? statusLabels[event.to_status] : "Status updated"}`}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {event.actor_name || "Original submitter"} · {formatFormTime(event.created_at)}
                  </p>
                  {event.note && (
                    <p className="mt-2 whitespace-pre-wrap break-words text-sm">{event.note}</p>
                  )}
                  {event.details && (
                    <dl className="mt-2 text-sm text-gray-600">
                      {Object.entries(event.details)
                        .filter(
                          ([key, value]) =>
                            [
                              "credit_reference",
                              "issued_date",
                              "scheduled_date",
                              "previous_scheduled_date",
                            ].includes(key) && value !== null
                        )
                        .map(([key, value]) => (
                          <div key={key}>
                            {key.replace(/_/g, " ")}: {String(value)}
                          </div>
                        ))}
                    </dl>
                  )}
                </li>
              ))}
            </ol>
          </div>
          <EmptyModal
            open={action !== null}
            dismissible={!busy}
            setOpen={() => {
              if (!busy) setAction(null);
            }}
            childComponent={
              action ? (
                <FormActionDialog
                  key={action}
                  form={form}
                  action={action}
                  onBusy={setBusy}
                  onClose={() => setAction(null)}
                  onDone={(updated) => {
                    setForm(updated);
                    setAction(null);
                  }}
                />
              ) : null
            }
          />
        </>
      )}
    </section>
  );
}
